import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud, Info, X, CheckCircle, AlertCircle, Loader, FileText, AlertTriangle } from 'lucide-react';
import { auth } from '../../../firebase/firebase.config';
import { importLeadsFromCSV, parseCSV, validateAllRows, MAX_FILE_SIZE } from '../../../firebase/csvImport.service';

/**
 * Desktop modal for importing leads from CSV files.
 * Full-featured: drag-drop, preview, validation, progress, results.
 */
export const ImportModal = ({ onClose, onImportComplete }) => {
    const fileInputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);

    // State machine: idle → preview → importing → results
    const [phase, setPhase] = useState('idle'); // 'idle' | 'preview' | 'importing' | 'results'

    // File and parsed data
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    // Import progress and results
    const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');

    // ========== FILE HANDLING ==========

    const handleFileSelect = useCallback(async (selectedFile) => {
        if (!selectedFile) return;

        // Validate file type
        if (!selectedFile.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        // Validate file size
        if (selectedFile.size > MAX_FILE_SIZE) {
            setError(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
            return;
        }

        setError('');
        setFile(selectedFile);
        setPhase('preview');

        // Parse CSV
        const parseResult = await parseCSV(selectedFile);
        if (!parseResult.success) {
            setError(parseResult.error);
            setPhase('idle');
            setFile(null);
            return;
        }

        setParsedData(parseResult.data);

        // Validate all rows
        const validation = validateAllRows(parseResult.data);
        setValidationResult(validation);
    }, []);

    const handleDrag = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0]);
        }
    }, [handleFileSelect]);

    const handleInputChange = useCallback((e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    }, [handleFileSelect]);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    // ========== IMPORT HANDLING ==========

    const handleImport = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            setError('Please sign in to import leads');
            return;
        }

        if (!file || !validationResult || validationResult.validLeads.length === 0) {
            setError('No valid leads to import');
            return;
        }

        setPhase('importing');
        setProgress({ current: 0, total: validationResult.validLeads.length, phase: 'starting' });

        try {
            const importResult = await importLeadsFromCSV(userId, file, (prog) => {
                setProgress(prog);
            });

            setResults(importResult);
            setPhase('results');

            if (onImportComplete) {
                onImportComplete(importResult);
            }
        } catch (err) {
            console.error('[ImportModal] Import error:', err);
            setError(err.message || 'Import failed');
            setPhase('preview');
        }
    };

    // ========== RESET ==========

    const handleReset = () => {
        setPhase('idle');
        setFile(null);
        setParsedData(null);
        setValidationResult(null);
        setProgress({ current: 0, total: 0, phase: '' });
        setResults(null);
        setError('');
    };

    // ========== RENDERING ==========

    // Render: Idle state (upload prompt)
    const renderIdle = () => (
        <>
            <div className="text-center mb-6">
                <div className="w-12 h-12 bg-notion-sidebar rounded-full flex items-center justify-center mx-auto mb-3 text-stone-400">
                    <UploadCloud className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg text-notion-text">Import Leads</h3>
                <p className="text-xs text-stone-400 mt-1">Bulk upload your CSV files.</p>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5 text-left">
                <p className="text-[10px] font-bold text-blue-800 uppercase mb-1 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Required Columns
                </p>
                <p className="text-[11px] text-blue-600 leading-relaxed">
                    Your CSV file must include these headers:{' '}
                    <span className="font-medium text-blue-800">First Name, Last Name, Email</span>.
                    Optional: Phone, Address, Tags, Status.
                </p>
                <a
                    href="/lead-import-template.csv"
                    download
                    className="text-[10px] text-blue-800 underline mt-2 block hover:text-blue-900 font-medium"
                >
                    Download Sample Template
                </a>
            </div>

            <div
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors cursor-pointer group ${dragActive
                        ? 'border-stone-400 bg-stone-100'
                        : 'border-stone-200 hover:bg-notion-sidebar'
                    }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleBrowseClick}
            >
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleInputChange}
                    className="hidden"
                />
                <p className="text-sm text-stone-600 font-medium group-hover:text-stone-800">
                    Click to upload or drag & drop
                </p>
                <p className="text-xs text-stone-400 mt-1">CSV files up to 10MB</p>
            </div>

            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
                <button onClick={onClose} className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition">
                    Cancel
                </button>
            </div>
        </>
    );

    // Render: Preview state (validation results)
    const renderPreview = () => (
        <>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-stone-500" />
                    <div>
                        <h3 className="font-semibold text-lg text-notion-text">Preview Import</h3>
                        <p className="text-xs text-stone-400">{file?.name}</p>
                    </div>
                </div>
                <button onClick={handleReset} className="text-stone-400 hover:text-stone-600">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-stone-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-stone-800">{parsedData?.length || 0}</p>
                    <p className="text-[10px] text-stone-500 uppercase">Total Rows</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-green-700">{validationResult?.validLeads?.length || 0}</p>
                    <p className="text-[10px] text-green-600 uppercase">Valid</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-red-700">{validationResult?.invalidRows?.length || 0}</p>
                    <p className="text-[10px] text-red-600 uppercase">Invalid</p>
                </div>
            </div>

            {/* Validation Errors */}
            {validationResult?.invalidRows?.length > 0 && (
                <div className="mb-5 max-h-40 overflow-y-auto">
                    <p className="text-[10px] font-bold text-red-800 uppercase mb-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Errors (rows will be skipped)
                    </p>
                    <div className="space-y-1">
                        {validationResult.invalidRows.slice(0, 10).map((row, idx) => (
                            <div key={idx} className="bg-red-50 border border-red-100 rounded p-2">
                                <p className="text-[11px] text-red-700">{row.errors.join(', ')}</p>
                            </div>
                        ))}
                        {validationResult.invalidRows.length > 10 && (
                            <p className="text-[10px] text-red-600 italic">
                                ...and {validationResult.invalidRows.length - 10} more errors
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Warnings */}
            {validationResult?.allWarnings?.length > 0 && (
                <div className="mb-5">
                    <p className="text-[10px] font-bold text-amber-700 uppercase mb-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Warnings
                    </p>
                    <div className="bg-amber-50 border border-amber-100 rounded p-2 max-h-20 overflow-y-auto">
                        <p className="text-[11px] text-amber-700">
                            {validationResult.allWarnings.slice(0, 5).join('; ')}
                            {validationResult.allWarnings.length > 5 && ` ...and ${validationResult.allWarnings.length - 5} more`}
                        </p>
                    </div>
                </div>
            )}

            {/* Preview Table */}
            {validationResult?.validLeads?.length > 0 && (
                <div className="mb-5">
                    <p className="text-[10px] font-bold text-stone-500 uppercase mb-2">Preview (first 5 valid leads)</p>
                    <div className="border border-stone-200 rounded-lg overflow-hidden">
                        <table className="w-full text-[11px]">
                            <thead className="bg-stone-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-stone-600 font-medium">Name</th>
                                    <th className="px-3 py-2 text-left text-stone-600 font-medium">Email</th>
                                    <th className="px-3 py-2 text-left text-stone-600 font-medium">Phone</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {validationResult.validLeads.slice(0, 5).map((lead, idx) => (
                                    <tr key={idx} className="hover:bg-stone-50">
                                        <td className="px-3 py-2 text-stone-800">{lead.fullName || '-'}</td>
                                        <td className="px-3 py-2 text-stone-600">{lead.email}</td>
                                        <td className="px-3 py-2 text-stone-600">{lead.phone || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                </div>
            )}

            <div className="flex justify-end gap-3">
                <button onClick={handleReset} className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition">
                    Choose Different File
                </button>
                <button
                    onClick={handleImport}
                    disabled={!validationResult?.validLeads?.length}
                    className={`px-4 py-2 text-xs font-medium rounded transition ${validationResult?.validLeads?.length
                            ? 'bg-stone-800 text-white hover:bg-stone-700'
                            : 'bg-stone-200 text-stone-400 cursor-not-allowed'
                        }`}
                >
                    Import {validationResult?.validLeads?.length || 0} Leads
                </button>
            </div>
        </>
    );

    // Render: Importing state (progress)
    const renderImporting = () => (
        <div className="text-center py-8">
            <Loader className="w-10 h-10 text-stone-400 animate-spin mx-auto mb-4" />
            <h3 className="font-semibold text-lg text-notion-text mb-2">Importing Leads...</h3>
            <p className="text-xs text-stone-500 mb-4">
                {progress.phase === 'creating' && 'Creating new leads...'}
                {progress.phase === 'updating' && 'Updating existing leads...'}
                {progress.phase === 'starting' && 'Starting import...'}
                {progress.phase === 'complete' && 'Finishing up...'}
            </p>

            {/* Progress bar */}
            <div className="w-full max-w-xs mx-auto bg-stone-200 rounded-full h-2 overflow-hidden">
                <div
                    className="bg-stone-800 h-full transition-all duration-300 ease-out"
                    style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
                />
            </div>
            <p className="text-[10px] text-stone-400 mt-2">
                {progress.current} of {progress.total} processed
            </p>
        </div>
    );

    // Render: Results state
    const renderResults = () => (
        <>
            <div className="text-center mb-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${results?.failed === 0 ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                    {results?.failed === 0 ? (
                        <CheckCircle className="w-6 h-6" />
                    ) : (
                        <AlertTriangle className="w-6 h-6" />
                    )}
                </div>
                <h3 className="font-semibold text-lg text-notion-text">Import Complete</h3>
            </div>

            {/* Results Stats */}
            <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-green-700">{results?.created || 0}</p>
                    <p className="text-[10px] text-green-600 uppercase">Created</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-blue-700">{results?.updated || 0}</p>
                    <p className="text-[10px] text-blue-600 uppercase">Updated</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-semibold text-red-700">{results?.failed || 0}</p>
                    <p className="text-[10px] text-red-600 uppercase">Failed</p>
                </div>
            </div>

            {/* Invalid rows summary */}
            {results?.invalidRows?.length > 0 && (
                <div className="mb-5 p-3 bg-stone-50 rounded-lg">
                    <p className="text-[10px] text-stone-500">
                        {results.invalidRows.length} rows were skipped due to validation errors
                    </p>
                </div>
            )}

            {/* Error details */}
            {results?.errors?.length > 0 && (
                <div className="mb-5 max-h-32 overflow-y-auto">
                    <p className="text-[10px] font-bold text-red-800 uppercase mb-2">Import Errors</p>
                    <div className="space-y-1">
                        {results.errors.slice(0, 5).map((err, idx) => (
                            <div key={idx} className="bg-red-50 border border-red-100 rounded p-2">
                                <p className="text-[11px] text-red-700">{err}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-end gap-3">
                <button onClick={handleReset} className="px-4 py-2 text-xs text-stone-500 hover:text-stone-800 transition">
                    Import Another
                </button>
                <button
                    onClick={onClose}
                    className="px-4 py-2 bg-stone-800 text-white text-xs font-medium rounded hover:bg-stone-700 transition"
                >
                    Done
                </button>
            </div>
        </>
    );

    return (
        <div className="fixed inset-0 bg-stone-900/20 backdrop-blur-sm z-50 flex items-center justify-center">
            <div className="bg-white w-[520px] max-h-[90vh] overflow-y-auto rounded-xl shadow-popover border border-notion-border p-8 fade-in">
                {phase === 'idle' && renderIdle()}
                {phase === 'preview' && renderPreview()}
                {phase === 'importing' && renderImporting()}
                {phase === 'results' && renderResults()}
            </div>
        </div>
    );
};

export default ImportModal;
